import _ from '@lodash';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormControl, Select, FormHelperText, InputLabel, MenuItem, ListItemText, Checkbox } from '@material-ui/core';
import { Lock, ArrowDropDown } from '@material-ui/icons';
import clsx from 'clsx';
import { determineEditableAccess } from '../EditFormUtils';

function EditFormSelectField(props) {
	const methods = useFormContext();
	const { control, getValues } = methods;
	const { t } = useTranslation(props.config.topic ? ['editform', props.config.topic] : 'editform');
	const { error } = props.fieldState;
	const { value, onChange } = props.field;
	const [items, setItems] = useState(null);
	const [readOnly, setReadOnly] = useState(determineEditableAccess(props.config, props.fieldConfig, props.fieldPrefix));
	const valueKey = !_.isUndefined(props.fieldConfig.type) && props.fieldConfig.type === 'enum' ? 'value' : 'id';
	const labelKey = !_.isUndefined(props.fieldConfig.type) && props.fieldConfig.type === 'enum' ? 'label' : 'name';
	const valueMode = props.fieldConfig.valueMode || 'id';
	const useObjectValue = valueMode === 'object';

	const parseFilterKey = () => {
		if (props.fieldConfig.filterKey) {
			if (_.isArray(props.fieldConfig.filterKey)) {
				return props.fieldConfig.filterKey.map(f => (!f.startsWith('^') ? (props.fieldPrefix || '') + f : f.substring(1)));
			}
			return !props.fieldConfig.filterKey.startsWith('^') ? (props.fieldPrefix || '') + props.fieldConfig.filterKey : props.fieldConfig.filterKey.substring(1);
		}
		return null;
	};

	const filterValue = useWatch({
		control,
		name: parseFilterKey()
		//defaultValue: null TODO: gondot okoz ha benne van
	});

	const updateItems = () => {
		if (typeof props.fieldConfig.items === 'function') {
			typeof props.fieldConfig.items(props.fieldConfig.itemsParameterIsDto ? getValues() : filterValue).then(result => {
				setItems(result || []);
			});
		} else if (Array.isArray(props.fieldConfig.items)) {
			setItems(props.fieldConfig.items);
		} else {
			setItems([]);
		}
	};

	useEffect(() => {
		updateItems();
	}, [props.fieldConfig, filterValue]);

	const itemMatchesValue = (item, selected) => {
		if (useObjectValue) {
			if (_.isObject(selected)) {
				return (!_.isUndefined(selected[valueKey]) && selected[valueKey] === item[valueKey]) || selected[labelKey] === item[labelKey];
			}
			return selected === item[valueKey] || selected === item[labelKey];
		}
		return selected === item[valueKey];
	};

	const findSelectedItem = selected => {
		if (_.isUndefined(selected) || selected === null || selected === '' || !items) return null;
		const itemIndex = items.findIndex(item => itemMatchesValue(item, selected));
		return itemIndex > -1 ? items[itemIndex] : null;
	};

	const getSelectValue = () => {
		if (props.fieldConfig.multiple) {
			if (!useObjectValue) return value || [];
			return (value || []).map(selected => findSelectedItem(selected)).filter(selected => selected !== null);
		}
		if (!useObjectValue) return value || '';
		return findSelectedItem(value) || '';
	};

	const renderValue = selected => {
		if (_.isArray(selected))
			return items
				.filter(d => selected.findIndex(s => itemMatchesValue(d, s)) > -1)
				.map(d => d[labelKey])
				.join(', ');
		if (!_.isUndefined(selected) && selected !== null && items) {
			const selectedItem = findSelectedItem(selected);
			if (selectedItem) return selectedItem[labelKey];
			if (_.isObject(selected)) return selected[labelKey] || '';
		}
		return '';
	};

	useEffect(() => {
		if (useObjectValue && items && !props.fieldConfig.multiple) {
			const selectedItem = findSelectedItem(value);
			if (selectedItem && selectedItem !== value) {
				onChange(selectedItem);
			}
		}
	}, [items]);

	useEffect(() => {
		!_.isUndefined(props.fieldConfig.onChange) ? props.fieldConfig.onChange(value, props.field, methods) : null;
	}, [value]);

	const handleChange = e => {
		const nextValue = e.target.value;
		onChange(nextValue);
		!_.isUndefined(props.fieldConfig.onUserChange) ? props.fieldConfig.onUserChange(nextValue, props.field, methods) : null;
	};

	return (
		<FormControl className={clsx('w-full mt-8 mb-16', props.fieldConfig.className)} variant="outlined" error={error} required={props.fieldConfig.required}>
			<InputLabel id={`${props.field.name}-label`}>{!_.isUndefined(props.fieldConfig.text) ? props.fieldConfig.text : t(`${props.config.topic}:${props.fieldConfig.key}`)}</InputLabel>
			{items != null ? (
				<Select
					labelId={`${props.field.name}-label`}
					id={props.field.name}
					multiple={!!props.fieldConfig.multiple}
					label={!_.isUndefined(props.fieldConfig.text) ? props.fieldConfig.text : t(`${props.config.topic}:${props.fieldConfig.key}`)}
					value={getSelectValue()}
					defaultValue={props.fieldConfig.multiple ? [] : ''}
					onChange={handleChange}
					inputProps={{ readOnly }}
					renderValue={selected => renderValue(selected)}
					IconComponent={readOnly ? Lock : ArrowDropDown}
				>
					<MenuItem value={null}>&nbsp;</MenuItem>
					{!_.isUndefined(props.fieldConfig.multiple) && props.fieldConfig.multiple
						? items.map((item, i) => (
								<MenuItem key={item[valueKey]} value={useObjectValue ? item : item[valueKey]}>
									<Checkbox checked={value && value.findIndex(selected => itemMatchesValue(item, selected)) > -1} />
									<ListItemText primary={item[labelKey]} />
								</MenuItem>
						  ))
						: items.map((item, i) => (
								<MenuItem key={item[valueKey]} value={useObjectValue ? item : item[valueKey]}>
									{item[labelKey]}
								</MenuItem>
						  ))}
				</Select>
			) : null}
			{error ? <FormHelperText>{error.message}</FormHelperText> : null}
		</FormControl>
	);
}

export default EditFormSelectField;
